import { useEffect, useRef, useState } from "react";

/**
 * Is this tile somewhere a person could actually be looking at it?
 *
 * False when the page is hidden (tab in the background, screen locked, another window
 * fullscreen over it) or when the tile itself is scrolled/laid out off the viewport.
 *
 * ## Why a wall needs this
 *
 * Concurrent video decode on the display is the wall's measured bottleneck
 * (docs/perf/decode-load.md), and a hidden tab decodes exactly as hard as a visible one.
 * Browsers throttle *timers* in background tabs but keep playing media, so a wall left on
 * a second monitor's background tab goes on decoding twelve streams and pulling a clip
 * every few seconds for nobody. Pausing there costs nothing — there is no picture to
 * miss — and it stops the fetching too, which is the same saving again on the link.
 *
 * The viewport half matters less on the wall itself, where every tile is on screen by
 * construction, and more everywhere else a region is rendered.
 *
 * Both signals are needed. `IntersectionObserver` does not fire for a hidden tab (the
 * geometry has not changed, so there is nothing to report), and `visibilitychange` says
 * nothing about a tile scrolled out of view.
 */
export function useTileOnscreen(ref: React.RefObject<Element | null>): boolean {
  // Assume visible until told otherwise: a tile that starts paused and waits for an
  // observer callback shows a black square on first paint.
  const [intersecting, setIntersecting] = useState(true);
  const [pageVisible, setPageVisible] = useState(
    () => typeof document === "undefined" || document.visibilityState !== "hidden",
  );

  useEffect(() => {
    const onChange = () => setPageVisible(document.visibilityState !== "hidden");
    document.addEventListener("visibilitychange", onChange);
    return () => document.removeEventListener("visibilitychange", onChange);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        const last = entries[entries.length - 1];
        // A visible SLIVER, not `isIntersecting` alone: the observer calls a box that
        // merely touches the edge of its clip intersecting, at ratio zero — every tile on
        // the page above or below a snap scroll's viewport sits exactly on that edge,
        // and would report on screen and decode unseen. The ratio is measured after the
        // scroller's own clipping, so it says what a person can actually see.
        if (last) setIntersecting(last.isIntersecting && last.intersectionRatio > 0);
      },
      // Any sliver counts as on screen. A tile half off the edge of a wall is still
      // being watched, and a real threshold would make it flicker between states as
      // the layout settles. The hair-thin second threshold is not a verdict (the ratio
      // rule above is) but a NOTIFICATION: a tile scrolling out of a snap scroller shows
      // a sliver mid-animation and then rests exactly on the edge, and that last step —
      // ratio above zero to ratio zero, `isIntersecting` unchanged — crosses no threshold
      // at zero alone, so the observer would stay silent and the tile would stay "on".
      { threshold: [0, 0.001] },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref]);

  return intersecting && pageVisible;
}

/** Read `useTileOnscreen` from callbacks that must not be rebuilt when it changes —
 *  timers and event handlers built once per source, which is most of RegionPlayer. */
export function useOnscreenRef(onscreen: boolean): React.MutableRefObject<boolean> {
  const ref = useRef(onscreen);
  ref.current = onscreen;
  return ref;
}
